import { CheckIcon, XIcon } from '@phosphor-icons/react'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { usePostRepositoryIntegration } from '../../utils/api-client/api-hooks.ts'
import { type CreateRepositoryIntegrationRequest, type Tool } from '../../utils/api-client/api-schemas.ts'
import {
  convertIntegrationTypeToTool,
  convertToolToIntegrationType,
  getIsMappableIntegrationType,
  getRepositoryIdentifierLabel,
  getRepositoryIdentifierLabels,
  getRepositoryIdentifierPlaceholder,
  MAPPABLE_INTEGRATION_TYPES,
} from '../../utils/repository-utils.tsx'
import { parsePositiveInteger } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import {
  IntegrationInstanceSelector,
  type IntegrationInstanceSelection,
} from '../input/integration-instance-selector.tsx'
import { TextInput } from '../input/text-input.tsx'
import { useIsMapToolFormOffered } from './map-tool-integration-form.hooks.ts'
import * as localStyles from './repository-settings-drawer.css.ts'

// #region Types

export type MapToolIntegrationFormValues = {
  selectedTool: Tool | undefined
  // The specific integration instance chosen in the picker. Distinguishes instances of the same
  // tool type (e.g. two Datadog connections), which `selectedTool` alone cannot.
  selectedIntegrationId: string | undefined
  // Whether that instance is configured in the admin console, as the picker reports it.
  isSelectedInstanceSystem: boolean
  projectKey: string
  // Black Duck Hub alone needs a second identifier: a Hub project has many versions and the bill of
  // materials hangs off the version, so `projectKey` names the project and this names the version.
  // Empty for every other tool, whose mapping is one identifier.
  versionId: string
}

export type MapToolIntegrationFormProps = {
  repositoryId: string
  onSuccess: () => unknown
  onCancel: () => unknown
  onUploadClick: (e: React.MouseEvent) => unknown
}

// #endregion

// #region Form Hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: MapToolIntegrationFormValues = {
  selectedTool: undefined,
  selectedIntegrationId: undefined,
  isSelectedInstanceSystem: false,
  projectKey: '',
  versionId: '',
}

export const useMapToolIntegrationForm = (defaultValueOverrides: Partial<MapToolIntegrationFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

// Both Hub identifiers are UUIDs rather than names on purpose: a name is a mutable label an operator
// can rename in the Hub console, which would orphan the mapping while leaving it syntactically
// valid. They are the two path segments of a project-version's own URL.
const HUB_PROJECT_ID_HELPER_TEXT = "The project UUID from the project-version's URL in the Black Duck Hub console."
const HUB_VERSION_ID_HELPER_TEXT =
  'The version UUID from the same URL. The bill of materials hangs off the version, not the project.'
const CHECKMARX_ENTERPRISE_PROJECT_ID_HELPER_TEXT =
  "The numeric project ID from the project's URL in the Checkmarx SAST portal."
// The platform reads the id into a Java `Integer`, so anything larger fails to deserialize.
const INT32_MAX = 2_147_483_647
const CHECKMARX_ENTERPRISE_PROJECT_ID_ERROR_TEXT = `Project ID must be a positive whole number no larger than ${INT32_MAX}, such as 13450.`

// A repository holds at most one mapping per tool and the endpoint has no PUT, so re-mapping an
// already-connected tool answers 409. The generic prefix gives the user nothing to act on; naming
// the cause points at the disconnect-then-reconnect that actually resolves it.
const SAVE_INTEGRATION_ERROR_OVERRIDES = {
  409: { message: 'This repository is already connected to this tool. Disconnect the existing connection first.' },
}

const parseCheckmarxEnterpriseProjectId = (value: string): number | undefined => {
  const projectId = parsePositiveInteger(value)
  return projectId !== undefined && projectId <= INT32_MAX ? projectId : undefined
}

/**
 * Builds the create-mapping body from the form's own values.
 *
 * The picker reports `{ type, integrationId }` and the form stores both, so the tool that decides
 * the body's shape and the instance id it names are already here — there is nothing to look up. This
 * used to resolve `selectedIntegrationId` against a list of every integration, which meant the form
 * and the picker had to be reading the same page of the collection for a pick to resolve at all.
 */
const buildIntegrationPayload = (
  values: MapToolIntegrationFormValues
): CreateRepositoryIntegrationRequest | undefined => {
  const { selectedTool, selectedIntegrationId, isSelectedInstanceSystem, projectKey, versionId } = values
  const type = selectedTool ? convertToolToIntegrationType(selectedTool) : undefined
  // `getIsMappableIntegrationType` rather than a `'polaris'` arm below: "Polaris has no user-entered
  // identifier, so it cannot be mapped by hand" is a rule `repository-utils` owns and pins to the
  // request union, and it is the same predicate `typeFilter` narrows the picker with. Narrowing here
  // leaves `match` exhaustive over `MappableIntegrationType` and this function with no "known type,
  // no body" case to return.
  if (type === undefined || selectedIntegrationId === undefined || !getIsMappableIntegrationType(type)) {
    return undefined
  }
  // Every mappable tool binds the chosen instance: each backend slice added `integration_id` to its
  // own `Create<Tool>RepositoryIntegrationRequest`, and as of this spec all six declare it. Naming it
  // in every arm rather than keeping a per-tool list here is what stops the two falling out of step.
  //
  // The platform looks a supplied id up among managed instances only, so an admin-console instance's
  // id (`sonar-default`) answers 422. Omitting the id is how a mapping names the admin-console one.
  const integrationId = isSelectedInstanceSystem ? undefined : selectedIntegrationId
  return (
    match(type)
      .with('sonar', () => ({
        type: 'sonar' as const,
        project_key: projectKey,
        integration_id: integrationId,
      }))
      .with('checkmarx', () => ({
        type: 'checkmarx' as const,
        project_id: projectKey,
        integration_id: integrationId,
      }))
      .with('appscan', () => ({
        type: 'appscan' as const,
        application_id: projectKey,
        integration_id: integrationId,
      }))
      .with('veracode', () => ({
        type: 'veracode' as const,
        application_guid: projectKey,
        integration_id: integrationId,
      }))
      .with('arnica', () => ({
        type: 'arnica' as const,
        asset_key: projectKey,
        integration_id: integrationId,
      }))
      .with('datadog', () => ({
        type: 'datadog' as const,
        repository_id: projectKey,
        integration_id: integrationId,
      }))
      // `integration_id` is required here rather than optional as it is for every other tool: Hub has
      // no system default instance to fall back to, so an unnamed instance has nothing to resolve to.
      .with('black-duck-hub', () => ({
        type: 'black-duck-hub' as const,
        project_id: projectKey,
        version_id: versionId,
        integration_id: selectedIntegrationId,
      }))
      .with('checkmarx-enterprise', (): CreateRepositoryIntegrationRequest | undefined => {
        const projectId = parseCheckmarxEnterpriseProjectId(projectKey)
        if (projectId === undefined) {
          return undefined
        }
        return { type: 'checkmarx-enterprise', project_id: projectId, integration_id: selectedIntegrationId }
      })
      .exhaustive()
  )
}

// #endregion

// #region MapToolIntegrationForm

export const MapToolIntegrationForm: React.FC<MapToolIntegrationFormProps> = ({
  repositoryId,
  onSuccess,
  onCancel,
  onUploadClick,
}) => {
  const form = useFormContext()
  const selectedTool = useSelector(form.store, state => state.values.selectedTool)
  const selectedIntegrationId = useSelector(form.store, state => state.values.selectedIntegrationId)
  const isSelectedInstanceSystem = useSelector(form.store, state => state.values.isSelectedInstanceSystem)
  const projectKey = useSelector(form.store, state => state.values.projectKey)
  const versionId = useSelector(form.store, state => state.values.versionId)

  const isFormOffered = useIsMapToolFormOffered()

  const { addToast } = useToastService()
  const showSaveIntegrationError = useShowMutationError({
    prefix: 'Failed to save integration',
    overrides: SAVE_INTEGRATION_ERROR_OVERRIDES,
  })
  const postRepositoryIntegration = usePostRepositoryIntegration({
    onError: showSaveIntegrationError,
  })

  // A tool with no user-entered identifier has no field to fill, so it cannot be mapped by hand;
  // `typeFilter` already keeps those out of the picker, and this keeps the two from disagreeing.
  const identifierLabel = getRepositoryIdentifierLabel(selectedTool)
  // The second of Hub's two identifiers, read from the same table the removal warning reads, so the
  // field and the warning cannot name it differently.
  const hubVersionLabel = getRepositoryIdentifierLabels(selectedTool)[1] ?? 'Version ID'

  const isBlackDuckHub = selectedTool === 'black_duck_hub_sca'
  const isProjectIdInvalid =
    selectedTool === 'checkmarx_enterprise' && parseCheckmarxEnterpriseProjectId(projectKey) === undefined
  const hasProjectIdError = isProjectIdInvalid && projectKey.trim() !== ''

  // Both are required: the payload is built from the chosen *instance*, so a tool without one
  // produced no body and `handleSubmit` returned without a word. Hub adds its version id, which the
  // platform requires: a mapping naming a project but no version has no bill of materials to read.
  const isSubmitDisabled =
    !selectedIntegrationId || !selectedTool || !projectKey || (isBlackDuckHub && !versionId) || isProjectIdInvalid

  // `selectedTool` stays in Tool terms because the project-key field is labelled per tool; the picker
  // reports the integration type, so convert on the way in.
  const handleInstanceChange = useCallback(
    ({ type, integrationId, isSystemInstance = false }: IntegrationInstanceSelection) => {
      const previousIntegrationId = form.getFieldValue('selectedIntegrationId')
      form.setFieldValue('selectedIntegrationId', integrationId)
      form.setFieldValue('isSelectedInstanceSystem', isSystemInstance)
      form.setFieldValue('selectedTool', type ? convertIntegrationTypeToTool(type) : undefined)
      // A version id belongs to the project-version of one Hub instance, so it is meaningless against
      // any other selection. Clearing it stops a switch away and back from restoring a value the user
      // never retyped for the instance now chosen.
      //
      // Only on a *move* between instances, though. The picker resolves a single-instance tool by
      // firing this on mount, and the host prefills an existing mapping's version id without an
      // instance id — so clearing unconditionally wiped the prefill before the drawer was painted.
      if (previousIntegrationId !== undefined && previousIntegrationId !== integrationId) {
        form.setFieldValue('versionId', '')
      }
    },
    [form]
  )

  const handleSubmit = useCallback(() => {
    const body = buildIntegrationPayload({
      selectedTool,
      selectedIntegrationId,
      isSelectedInstanceSystem,
      projectKey,
      versionId,
    })
    // Save is gated on a resolvable instance, so a missing body is a bug rather than a user state.
    invariant(body, 'Cannot save a repository integration without a selected instance')
    postRepositoryIntegration.mutate(
      { repositoryId, body },
      {
        onSuccess: () => {
          addToast({ message: <>Integration saved successfully.</>, variant: 'success' })
          onSuccess()
        },
      }
    )
  }, [
    selectedTool,
    selectedIntegrationId,
    isSelectedInstanceSystem,
    projectKey,
    versionId,
    postRepositoryIntegration,
    repositoryId,
    addToast,
    onSuccess,
  ])

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}
      className={localStyles.mapNewForm}
      onSubmit={e => e.preventDefault()}
    >
      {isFormOffered ? (
        <IntegrationInstanceSelector
          value={{
            type: selectedTool ? convertToolToIntegrationType(selectedTool) : undefined,
            integrationId: selectedIntegrationId,
          }}
          onChange={handleInstanceChange}
          typeFilter={MAPPABLE_INTEGRATION_TYPES}
          toolLabel="Available tools"
          instanceLabel="Instance"
          isInstanceRequired
        />
      ) : (
        <div className={localStyles.noToolsContainer} data-testid="no-tools-container">
          <div className={localStyles.noToolsContent}>
            <span className={localStyles.noToolsHeading}>No tools have been connected</span>
            <a href="" className={localStyles.uploadLink} onClick={onUploadClick}>
              Upload a SARIF file instead
            </a>
          </div>
        </div>
      )}
      {identifierLabel !== undefined && (
        <TextInput
          id="projectKey"
          label={identifierLabel}
          placeholder={getRepositoryIdentifierPlaceholder(selectedTool)}
          value={projectKey}
          helperText={match(selectedTool)
            .with('black_duck_hub_sca', () => HUB_PROJECT_ID_HELPER_TEXT)
            .with('checkmarx_enterprise', () => CHECKMARX_ENTERPRISE_PROJECT_ID_HELPER_TEXT)
            .otherwise(() => undefined)}
          errorText={hasProjectIdError ? CHECKMARX_ENTERPRISE_PROJECT_ID_ERROR_TEXT : undefined}
          isError={hasProjectIdError}
          onChange={value => form.setFieldValue('projectKey', value)}
        />
      )}
      {isBlackDuckHub && (
        <TextInput
          id="versionId"
          label={hubVersionLabel}
          placeholder="Enter version ID"
          value={versionId}
          helperText={HUB_VERSION_ID_HELPER_TEXT}
          onChange={value => form.setFieldValue('versionId', value)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'end', gap: '16px', width: '100%' }}>
        <GhostButton type="secondary" onClick={onCancel} ariaLabel="Cancel">
          <XIcon size={16} weight="regular" />
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={postRepositoryIntegration.isPending ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
          onClick={handleSubmit}
          ariaLabel="Save"
        >
          <CheckIcon size={16} weight="regular" />
        </DefaultButton>
      </div>
    </form>
  )
}

// #endregion
